/**
 * Fila de sugestões de IA do Consolidador.
 *
 * Fala com os mesmos endpoints de curadoria que o CRM usa — não existe fila paralela. Aceitar e
 * recusar reusam as rotas de `aawz-intelligence-v2` sem redefinir nada; o que esta classe
 * acrescenta é o recorte do consolidador, que vive em `utils/suggestions-fila/`.
 *
 * `carregarFila` é sempre chamada com `idClienteApp`: as duas superfícies vivem dentro da carteira
 * de UM cliente. Sem ele o endpoint devolveria a fila do escritório inteiro, o que pintaria badge
 * de carteira alheia — por isso os chamadores fazem o guard antes, e não existe uso sem escopo.
 */
import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { urlDaFila } from '@modules/consolidator/utils/suggestions-query/suggestions-query'
import {
  agruparPorStatus,
  apenasDoConsolidador,
} from '@modules/consolidator/utils/suggestions-fila/suggestions-fila'
import {
  envelopeDeErro,
  listaDaResposta,
} from '@modules/consolidator/utils/suggestions-resposta/suggestions-resposta'

class APIConsolidatorSuggestions extends APITemplate {
  async carregarFila({ idClienteApp = null } = {}) {
    // Fail-closed: sem cliente a URL vira a fila do ESCRITORIO inteiro. Os chamadores ja fazem o
    // guard, mas confiar nisso deixa a porta aberta para o proximo chamador.
    if (!idClienteApp) {
      return this.buildErrorResponse({
        status: 400,
        message: 'Não foi possível identificar o cliente das sugestões.',
      })
    }
    const r = await con.get(urlDaFila({ idClienteApp }))
    if (!r || r.status !== 200) return this.buildErrorResponse(envelopeDeErro(r))

    const doConsolidador = apenasDoConsolidador(listaDaResposta(r))

    return this.buildSuccessResponse({
      value: {
        suggestions: doConsolidador,
        porStatus: agruparPorStatus(doConsolidador),
      },
    })
  }

  async aceitar({ id, dados = null } = {}) {
    if (!id) {
      return this.buildErrorResponse({ status: 400, message: 'id obrigatório' })
    }
    const body = dados ? { dados: JSON.stringify(dados) } : {}
    const r = await con.post(intelligenceV2.suggestionAccept(id), body)
    return (
      r ||
      this.buildErrorResponse({
        message: 'Não foi possível aceitar a sugestão.',
      })
    )
  }

  async recusar({ id, motivo = null } = {}) {
    if (!id) {
      return this.buildErrorResponse({ status: 400, message: 'id obrigatório' })
    }
    const body = motivo ? { motivo } : {}
    const r = await con.post(intelligenceV2.suggestionDecline(id), body)
    return (
      r ||
      this.buildErrorResponse({
        message: 'Não foi possível recusar a sugestão.',
      })
    )
  }
}

const apiConsolidatorSuggestions = new APIConsolidatorSuggestions()
export { apiConsolidatorSuggestions }
